/**
 * templateMetadata.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Central metadata registry for all ResumeBar templates (both standard and premium).
 * Exposes feature flags, rating breakdowns, compatibility parameters, complexity ratings,
 * theme options, and scoring weights for the recommendation engine.
 */

export const TEMPLATE_METADATA = {
  // ─── STANDARD COLLECTION (UNTOCUHED PRODUCTION TEMPLATES) ─────────────────
  'harvard-classic': {
    id: 'harvard-classic',
    name: 'Harvard ATS Classic',
    category: 'ats_professional',
    premium: false,
    isNew: false,
    schemaVersion: 2,
    layoutVersion: 1,
    assetVersion: 1,
    complexity: 'easy',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 100, visual: 75, recruiter: 95, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Software Engineer', 'Investment Banker', 'Consultant', 'Lawyer', 'Manager'],
      industries: ['Finance', 'Legal', 'Management Consulting', 'Government', 'Technology'],
      experience: ['Mid', 'Senior', 'Leadership'],
      keywords: ['classic', 'clean', 'monochrome', 'ats-perfect', 'single-column'],
      weight: 100
    },
    themes: [
      { id: 'charcoal', primary: '#2f2f2f' },
      { id: 'classic-navy', primary: '#1a1a2e' }
    ],
    tags: ['ATS', 'Classic', 'Minimalist', 'Monochrome', 'Traditional'],
    assets: {
      card: null, // Falls back to scaled React rendering
      preview: null,
      full: null
    }
  },
  'modern-ats-pro': {
    id: 'modern-ats-pro',
    name: 'Modern ATS Pro',
    category: 'ats_professional',
    premium: false,
    isNew: false,
    schemaVersion: 2,
    layoutVersion: 1,
    assetVersion: 1,
    complexity: 'easy',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 98, visual: 85, recruiter: 96, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: true,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: true
    },
    recommendation: {
      roles: ['Software Engineer', 'Product Manager', 'Data Scientist', 'Designer', 'Engineer'],
      industries: ['Technology', 'Startups', 'Consulting', 'Software Dev'],
      experience: ['Junior', 'Mid', 'Senior'],
      keywords: ['modern', 'blue', 'accent', 'icons', 'clean'],
      weight: 98
    },
    themes: [
      { id: 'blue', primary: '#2563EB' },
      { id: 'emerald', primary: '#059669' }
    ],
    tags: ['ATS', 'Modern', 'Icons', 'Clean', 'Blue'],
    assets: {
      card: null,
      preview: null,
      full: null
    }
  },
  'faang-engineering': {
    id: 'faang-engineering',
    name: 'FAANG Engineer',
    category: 'developer',
    premium: true,
    isNew: true,
    schemaVersion: 2,
    layoutVersion: 2,
    assetVersion: 1,
    complexity: 'medium',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 99, visual: 85, recruiter: 98, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Software Engineer', 'Backend Developer', 'Frontend Developer', 'Tech Lead', 'DevOps Engineer', 'Systems Programmer'],
      industries: ['Technology', 'Software Dev', 'AI / ML', 'Cloud Platforms'],
      experience: ['Junior', 'Mid', 'Senior', 'Leadership'],
      keywords: ['faang', 'tech', 'skills-first', 'ultra-dense', 'single-column', 'clean-margins'],
      weight: 99
    },
    themes: [
      { id: 'cyan', primary: '#0891b2' },
      { id: 'slate', primary: '#1e293b' }
    ],
    tags: ['ATS', 'Tech', 'FAANG', 'Monochrome', 'Skills-First', 'Developer-Family'],
    assets: {
      card: '/templates/cards/faang-engineering.webp',
      preview: '/templates/previews/faang-engineering.webp',
      full: '/templates/full/faang-engineering.webp'
    }
  },
  'executive-professional': {
    id: 'executive-professional',
    name: 'Executive Professional',
    category: 'executive',
    premium: false,
    isNew: false,
    schemaVersion: 2,
    layoutVersion: 1,
    assetVersion: 1,
    complexity: 'medium',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 97, visual: 85, recruiter: 97, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: true,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: true,
      ratingBars: false
    },
    recommendation: {
      roles: ['CEO', 'VP', 'Director', 'General Manager', 'Consultant'],
      industries: ['Finance', 'Consulting', 'Executive Leadership', 'MNCs'],
      experience: ['Senior', 'Leadership'],
      keywords: ['executive', 'professional', 'c-suite', 'heavy', 'formal'],
      weight: 97
    },
    themes: [
      { id: 'slate', primary: '#0f172a' },
      { id: 'navy', primary: '#1e3a5f' }
    ],
    tags: ['Executive', 'C-Suite', 'Formal', 'Heavy', 'Trust'],
    assets: {
      card: null,
      preview: null,
      full: null
    }
  },
  'campus-placement': {
    id: 'campus-placement',
    name: 'Campus Placement',
    category: 'student',
    premium: false,
    isNew: false,
    schemaVersion: 2,
    layoutVersion: 1,
    assetVersion: 1,
    complexity: 'easy',
    supports: { resume: true, coverLetter: false, portfolio: false },
    ratings: { ats: 96, visual: 70, recruiter: 90, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Student', 'Graduate', 'Intern', 'Software Engineering Intern', 'Analyst'],
      industries: ['Academic', 'Freshers', 'Campus Placement'],
      experience: ['Student', 'Junior'],
      keywords: ['education-first', 'freshers', 'campus', 'iit', 'nit'],
      weight: 96
    },
    themes: [
      { id: 'navy', primary: '#1e3a5f' },
      { id: 'indigo', primary: '#4f46e5' }
    ],
    tags: ['Student', 'Freshers', 'Campus', 'Education-First'],
    assets: {
      card: null,
      preview: null,
      full: null
    }
  },
  'internship-hunter': {
    id: 'internship-hunter',
    name: 'Internship Hunter',
    category: 'student',
    premium: false,
    isNew: false,
    schemaVersion: 2,
    layoutVersion: 1,
    assetVersion: 1,
    complexity: 'easy',
    supports: { resume: true, coverLetter: false, portfolio: false },
    ratings: { ats: 95, visual: 75, recruiter: 91, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: true,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: true
    },
    recommendation: {
      roles: ['Student', 'Intern', 'Fresh Graduate', 'Hackathon Participant'],
      industries: ['Academic', 'Freshers', 'Technology', 'Startups'],
      experience: ['Student', 'Junior'],
      keywords: ['projects-first', 'skills-first', 'internship', 'easy'],
      weight: 95
    },
    themes: [
      { id: 'purple', primary: '#7C3AED' },
      { id: 'blue', primary: '#2563EB' }
    ],
    tags: ['Student', 'Internship', 'Projects-First', 'Modern'],
    assets: {
      card: null,
      preview: null,
      full: null
    }
  },
  'fresher-plus': {
    id: 'fresher-plus',
    name: 'Fresher Plus',
    category: 'student',
    premium: false,
    isNew: false,
    schemaVersion: 2,
    layoutVersion: 1,
    assetVersion: 1,
    complexity: 'easy',
    supports: { resume: true, coverLetter: false, portfolio: false },
    ratings: { ats: 96, visual: 72, recruiter: 91, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Student', 'Fresh Graduate', 'Entry Level Assistant', 'Sales Associate'],
      industries: ['Academic', 'Freshers', 'Retail', 'Generalist'],
      experience: ['Student', 'Junior'],
      keywords: ['balanced', 'education', 'fresher', 'clean'],
      weight: 96
    },
    themes: [
      { id: 'green', primary: '#059669' },
      { id: 'slate', primary: '#475569' }
    ],
    tags: ['Student', 'Fresher', 'Balanced', 'Clean'],
    assets: {
      card: null,
      preview: null,
      full: null
    }
  },
  'finance-pro': {
    id: 'finance-pro',
    name: 'Finance Pro',
    category: 'ats_professional',
    premium: false,
    isNew: false,
    schemaVersion: 2,
    layoutVersion: 1,
    assetVersion: 1,
    complexity: 'medium',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 98, visual: 78, recruiter: 95, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Chartered Accountant', 'Finance Analyst', 'CFA Candidate', 'Investment Banker'],
      industries: ['Finance', 'Banking', 'Audit', 'Corporate Finance'],
      experience: ['Mid', 'Senior'],
      keywords: ['finance', 'metrics', 'two-zone', 'traditional', 'cfa'],
      weight: 98
    },
    themes: [
      { id: 'brown', primary: '#92400e' },
      { id: 'navy', primary: '#1e3a5f' }
    ],
    tags: ['ATS', 'Finance', 'Traditional', 'CFA/CA', 'Metrics'],
    assets: {
      card: null,
      preview: null,
      full: null
    }
  },
  'product-consulting': {
    id: 'product-consulting',
    name: 'McKinsey Consultant',
    category: 'ats_professional',
    premium: true,
    isNew: true,
    schemaVersion: 2,
    layoutVersion: 2,
    assetVersion: 1,
    complexity: 'medium',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 98, visual: 92, recruiter: 98, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Management Consultant', 'Strategy Associate', 'Engagement Manager', 'CFA / CA Analyst', 'MBA Graduate'],
      industries: ['Management Consulting', 'MBB Firms', 'Corporate Strategy', 'Private Equity'],
      experience: ['Mid', 'Senior', 'Leadership'],
      keywords: ['mbb', 'mckinsey', 'achievement-first', 'garamond', 'spacious-margins', 'traditional-elegant'],
      weight: 98
    },
    themes: [
      { id: 'slate', primary: '#334155' },
      { id: 'navy', primary: '#1e3a5f' }
    ],
    tags: ['ATS', 'Consulting', 'McKinsey', 'MBA', 'Whitespace', 'Serif', 'Harvard-Family'],
    assets: {
      card: '/templates/cards/product-consulting.webp',
      preview: '/templates/previews/product-consulting.webp',
      full: '/templates/full/product-consulting.webp'
    }
  },
  'creative-professional': {
    id: 'creative-professional',
    name: 'Creative Professional',
    category: 'creative',
    premium: false,
    isNew: false,
    schemaVersion: 2,
    layoutVersion: 1,
    assetVersion: 1,
    complexity: 'advanced',
    supports: { resume: true, coverLetter: true, portfolio: true },
    ratings: { ats: 88, visual: 96, recruiter: 93, print: 85 },
    features: {
      photo: true,
      sidebar: true,
      icons: true,
      timeline: true,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: true
    },
    recommendation: {
      roles: ['UI/UX Designer', 'Graphic Designer', 'Marketing Specialist', 'Content Creator'],
      industries: ['Creative Agencies', 'Design', 'Marketing', 'Media'],
      experience: ['Junior', 'Mid', 'Senior'],
      keywords: ['creative', 'sidebar', 'portfolio', 'visual', 'photo'],
      weight: 88
    },
    themes: [
      { id: 'purple', primary: '#7C3AED' },
      { id: 'pink', primary: '#db2777' }
    ],
    tags: ['Creative', 'Sidebar', 'Photo', 'Designer', 'Portfolio'],
    assets: {
      card: null,
      preview: null,
      full: null
    }
  },
  'clean-minimal-ats': {
    id: 'clean-minimal-ats',
    name: 'Clean Minimal ATS',
    category: 'elegant',
    premium: false,
    isNew: false,
    schemaVersion: 2,
    layoutVersion: 1,
    assetVersion: 1,
    complexity: 'easy',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 99, visual: 80, recruiter: 95, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Software Engineer', 'Data Analyst', 'HR Specialist', 'Operations Manager', 'Teacher'],
      industries: ['Technology', 'Corporate', 'HR', 'Education', 'Legal'],
      experience: ['Junior', 'Mid', 'Senior', 'Leadership'],
      keywords: ['minimal', 'clean', 'whitespace', 'charcoal', 'readability'],
      weight: 99
    },
    themes: [
      { id: 'dark-gray', primary: '#111827' },
      { id: 'indigo', primary: '#4f46e5' }
    ],
    tags: ['ATS', 'Minimal', 'Whitespace', 'Clean', 'Any Role'],
    assets: {
      card: null,
      preview: null,
      full: null
    }
  },
  'two-column-ats': {
    id: 'two-column-ats',
    name: 'Two Column ATS',
    category: 'ats_professional',
    premium: false,
    isNew: false,
    schemaVersion: 2,
    layoutVersion: 1,
    assetVersion: 1,
    complexity: 'medium',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 97, visual: 85, recruiter: 94, print: 100 },
    features: {
      photo: false,
      sidebar: true,
      icons: true,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: true
    },
    recommendation: {
      roles: ['Software Engineer', 'Data Scientist', 'Business Analyst', 'Systems Engineer'],
      industries: ['Technology', 'Finance', 'Analytics', 'MNCs'],
      experience: ['Mid', 'Senior'],
      keywords: ['two-column', 'sidebar', 'ats-friendly', 'skills-column'],
      weight: 97
    },
    themes: [
      { id: 'navy', primary: '#1e3a5f' },
      { id: 'slate', primary: '#334155' }
    ],
    tags: ['ATS', 'Two-Column', 'Sidebar', 'Skills-First'],
    assets: {
      card: null,
      preview: null,
      full: null
    }
  },

  // ─── PREMIUM COLLECTION — 5 FAMILY TEMPLATE SYSTEM ─────────────────────────
  // Family: Harvard (Academic Serif) · Executive · Developer · Modern · Student
  // Each template has a distinct philosophy, hierarchy, and visual identity.

  // ── Harvard Family ──────────────────────────────────────────────────────────
  'stanford-ats': {
    id: 'stanford-ats',
    name: 'Harvard Classic',
    category: 'ats_professional',
    premium: true,
    isNew: true,
    schemaVersion: 2,
    layoutVersion: 2,
    assetVersion: 1,
    complexity: 'easy',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 100, visual: 80, recruiter: 97, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Investment Banker', 'Management Consultant', 'Corporate Lawyer', 'Chartered Accountant', 'Research Fellow'],
      industries: ['Finance', 'Legal', 'Academic', 'Consulting', 'Government'],
      experience: ['Mid', 'Senior', 'Leadership'],
      keywords: ['serif', 'academic', 'centered-header', 'dotted-dividers', 'cardinal-red', 'luxury-spacing'],
      weight: 99
    },
    themes: [
      { id: 'cardinal-red', primary: '#8B0000' },
      { id: 'monochrome', primary: '#111111' }
    ],
    tags: ['ATS', 'Academic', 'Serif', 'Centered', 'Law', 'Finance', 'Harvard-Family'],
    assets: {
      card: '/templates/cards/stanford-ats.webp',
      preview: '/templates/previews/stanford-ats.webp',
      full: '/templates/full/stanford-ats.webp'
    }
  },
  // ── Modern Family ────────────────────────────────────────────────────────────
  'modern-blue': {
    id: 'modern-blue',
    name: 'Modern Professional',
    category: 'modern_professional',
    premium: true,
    isNew: true,
    schemaVersion: 2,
    layoutVersion: 2,
    assetVersion: 1,
    complexity: 'medium',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 98, visual: 90, recruiter: 97, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Product Manager', 'Senior Engineer', 'Business Analyst', 'Management Consultant', 'Data Scientist'],
      industries: ['Technology', 'Consulting', 'MNCs', 'Product Companies'],
      experience: ['Mid', 'Senior'],
      keywords: ['single-column', 'impact-bullets', 'left-bar-dividers', 'inter', 'cobalt', 'skills-second'],
      weight: 98
    },
    themes: [
      { id: 'cobalt', primary: '#2563EB' },
      { id: 'indigo', primary: '#4338CA' }
    ],
    tags: ['Modern', 'Single-Column', 'Impact-Bullets', 'Left-Bar', 'Professional', 'Modern-Family'],
    assets: {
      card: '/templates/cards/modern-blue.webp',
      preview: '/templates/previews/modern-blue.webp',
      full: '/templates/full/modern-blue.webp'
    }
  },
  'creative-studio': {
    id: 'creative-studio',
    name: 'Creative Studio',
    category: 'creative',
    premium: true,
    isNew: true,
    schemaVersion: 2,
    layoutVersion: 2,
    assetVersion: 1,
    complexity: 'advanced',
    supports: { resume: true, coverLetter: true, portfolio: true },
    ratings: { ats: 84, visual: 99, recruiter: 96, print: 88 },
    features: {
      photo: false,
      sidebar: true,
      icons: false,
      timeline: true,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['UI/UX Designer', 'Brand Designer', 'Creative Director', 'Art Director', 'Product Designer'],
      industries: ['Creative Agencies', 'Design Studios', 'Media', 'Advertising', 'Tech (Design Roles)'],
      experience: ['Junior', 'Mid', 'Senior'],
      keywords: ['colored-sidebar', 'timeline-experience', 'cards-projects', 'tag-skills', 'no-dividers', 'whitespace-design'],
      weight: 86
    },
    themes: [
      { id: 'indigo', primary: '#4F46E5' },
      { id: 'rose', primary: '#E11D48' }
    ],
    tags: ['Creative', 'Colored-Sidebar', 'Timeline', 'Cards', 'No-Dividers', 'Designer', 'Modern-Family'],
    assets: {
      card: '/templates/cards/creative-studio.webp',
      preview: '/templates/previews/creative-studio.webp',
      full: '/templates/full/creative-studio.webp'
    }
  },
  // ── Executive Family ─────────────────────────────────────────────────────────
  'executive-black': {
    id: 'executive-black',
    name: 'Executive Black',
    category: 'executive',
    premium: true,
    isNew: true,
    schemaVersion: 2,
    layoutVersion: 2,
    assetVersion: 1,
    complexity: 'advanced',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 97, visual: 96, recruiter: 99, print: 100 },
    features: {
      photo: false,
      sidebar: false,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: true,
      ratingBars: false
    },
    recommendation: {
      roles: ['CEO', 'VP', 'Managing Director', 'CXO', 'Senior Partner', 'Board Member'],
      industries: ['Finance', 'Consulting', 'Executive Leadership', 'MNCs', 'Private Equity'],
      experience: ['Senior', 'Leadership'],
      keywords: ['single-column', 'full-bar-dividers', 'luxury-spacing', 'playfair-serif', 'gold-accent', 'achievements-first'],
      weight: 97
    },
    themes: [
      { id: 'jet-black', primary: '#0F0F0F' },
      { id: 'luxury-gold', primary: '#C9A84C' }
    ],
    tags: ['Executive', 'Luxury', 'Single-Column', 'Full-Bar-Dividers', 'Serif', 'C-Suite', 'Executive-Family'],
    assets: {
      card: '/templates/cards/executive-black.webp',
      preview: '/templates/previews/executive-black.webp',
      full: '/templates/full/executive-black.webp'
    }
  },
  // ── Developer Family ─────────────────────────────────────────────────────────
  'startup-neon': {
    id: 'startup-neon',
    name: 'Startup Engineer',
    category: 'startup',
    premium: true,
    isNew: true,
    schemaVersion: 2,
    layoutVersion: 2,
    assetVersion: 1,
    complexity: 'medium',
    supports: { resume: true, coverLetter: true, portfolio: true },
    ratings: { ats: 88, visual: 96, recruiter: 93, print: 82 },
    features: {
      photo: false,
      sidebar: true,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Full-Stack Engineer', 'AI/ML Engineer', 'Product Engineer', 'Early Startup Hire', 'Backend Developer'],
      industries: ['Startups', 'AI / ML', 'Seed-Stage Companies', 'Venture-Backed Tech'],
      experience: ['Junior', 'Mid'],
      keywords: ['dark-sidebar', 'neon-violet', 'metrics-experience', 'feature-block-projects', 'pill-dividers', 'tag-skills'],
      weight: 90
    },
    themes: [
      { id: 'neon-violet', primary: '#A855F7' },
      { id: 'neon-green', primary: '#22C55E' }
    ],
    tags: ['Startup', 'Dark-Sidebar', 'Neon', 'Metrics', 'Feature-Blocks', 'Pill-Dividers', 'Developer-Family'],
    assets: {
      card: '/templates/cards/startup-neon.webp',
      preview: '/templates/previews/startup-neon.webp',
      full: '/templates/full/startup-neon.webp'
    }
  },
  // ── Student Family ───────────────────────────────────────────────────────────
  'university-campus': {
    id: 'university-campus',
    name: 'Campus Placement',
    category: 'student',
    premium: true,
    isNew: true,
    schemaVersion: 2,
    layoutVersion: 2,
    assetVersion: 1,
    complexity: 'easy',
    supports: { resume: true, coverLetter: false, portfolio: false },
    ratings: { ats: 97, visual: 88, recruiter: 95, print: 100 },
    features: {
      photo: false,
      sidebar: true,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['B.Tech Graduate', 'BCA / BBA Student', 'Campus Hire', 'Software Intern', 'Graduate Trainee'],
      industries: ['Academic', 'Campus Placement Drives', 'IT Services', 'Fresher-Hiring Firms'],
      experience: ['Student', 'Junior'],
      keywords: ['education-first', 'two-column', 'card-projects', 'pill-dividers', 'cgpa-sidebar', 'tag-skills'],
      weight: 97
    },
    themes: [
      { id: 'campus-blue', primary: '#1D4ED8' },
      { id: 'teal', primary: '#0D9488' }
    ],
    tags: ['Student', 'Campus', 'Two-Column', 'Cards', 'Education-First', 'Pill-Dividers', 'Student-Family'],
    assets: {
      card: '/templates/cards/university-campus.webp',
      preview: '/templates/previews/university-campus.webp',
      full: '/templates/full/university-campus.webp'
    }
  },
  'github-developer': {
    id: 'github-developer',
    name: 'GitHub Developer',
    category: 'developer',
    premium: true,
    isNew: true,
    schemaVersion: 2,
    layoutVersion: 2,
    assetVersion: 1,
    complexity: 'medium',
    supports: { resume: true, coverLetter: true, portfolio: true },
    ratings: { ats: 97, visual: 93, recruiter: 96, print: 100 },
    features: {
      photo: false,
      sidebar: true,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Software Engineer', 'Backend Developer', 'Frontend Developer', 'Fullstack Engineer', 'SDE-I / SDE-II'],
      industries: ['Software Dev', 'Technology', 'FAANG', 'Cloud Services', 'Open Source'],
      experience: ['Junior', 'Mid', 'Senior'],
      keywords: ['right-sidebar', 'monospace', 'compact', 'badge-grid-skills', 'card-projects', 'left-bar-dividers', 'skills-first'],
      weight: 97
    },
    themes: [
      { id: 'github-dark', primary: '#1F2328' },
      { id: 'github-green', primary: '#2DA44E' }
    ],
    tags: ['Developer', 'Monospace', 'Compact', 'Badge-Grid', 'Right-Sidebar', 'Cards', 'Left-Bar-Dividers', 'Developer-Family'],
    assets: {
      card: '/templates/cards/github-developer.webp',
      preview: '/templates/previews/github-developer.webp',
      full: '/templates/full/github-developer.webp'
    }
  },
  // ── Finance Family ───────────────────────────────────────────────────────────
  'terminal-hacker': {
    id: 'terminal-hacker',
    name: 'Finance & Consulting Pro',
    category: 'ats_professional',
    premium: true,
    isNew: true,
    schemaVersion: 2,
    layoutVersion: 2,
    assetVersion: 1,
    complexity: 'medium',
    supports: { resume: true, coverLetter: true, portfolio: false },
    ratings: { ats: 99, visual: 89, recruiter: 97, print: 100 },
    features: {
      photo: false,
      sidebar: true,
      icons: false,
      timeline: false,
      charts: false,
      qrCode: false,
      signature: false,
      ratingBars: false
    },
    recommendation: {
      roles: ['Chartered Accountant', 'CFA / CPA Candidate', 'Investment Banker', 'Management Consultant', 'Finance Analyst', 'Audit Manager'],
      industries: ['Finance', 'Banking', 'Consulting', 'Audit', 'Private Equity', 'Corporate Finance'],
      experience: ['Mid', 'Senior', 'Leadership'],
      keywords: ['right-sidebar', 'georgia-serif', 'impact-bullets', 'compact-density', 'teal-accent', 'traditional-dividers', 'summary-first'],
      weight: 98
    },
    themes: [
      { id: 'teal', primary: '#0D6E6E' },
      { id: 'navy', primary: '#1E3A5F' }
    ],
    tags: ['Finance', 'Consulting', 'CA/CFA', 'Serif', 'Right-Sidebar', 'Impact-Bullets', 'Compact', 'Finance-Family'],
    assets: {
      card: '/templates/cards/terminal-hacker.webp',
      preview: '/templates/previews/terminal-hacker.webp',
      full: '/templates/full/terminal-hacker.webp'
    }
  }
};

// Marketplace Categories / Collections
export const TEMPLATE_CATEGORIES = [
  { id: 'ats_professional', label: 'ATS Collection', badge: 'ATS ✓', badgeColor: '#059669', description: 'Guaranteed to pass any ATS scanner', templates: ['harvard-classic', 'modern-ats-pro', 'finance-pro', 'clean-minimal-ats', 'two-column-ats', 'stanford-ats'] },
  { id: 'modern_professional', label: 'Modern Collection', badge: 'Modern', badgeColor: '#2563EB', description: 'Clean layouts with subtle colors', templates: ['product-consulting', 'modern-blue', 'terminal-hacker'] },
  { id: 'creative', label: 'Creative Collection', badge: 'Creative', badgeColor: '#db2777', description: 'Visual and design-centric layouts', templates: ['creative-professional', 'creative-studio'] },
  { id: 'executive', label: 'Executive Collection', badge: 'Executive', badgeColor: '#D4AF37', description: 'Premium luxury look for leadership', templates: ['executive-professional', 'executive-black'] },
  { id: 'startup', label: 'Startup Collection', badge: 'Startup', badgeColor: '#a855f7', description: 'Modern neon-accented tech themes', templates: ['startup-neon'] },
  { id: 'student', label: 'Student Collection', badge: 'Student', badgeColor: '#0ea5e9', description: 'Education and fresher-first designs', templates: ['campus-placement', 'internship-hunter', 'fresher-plus', 'university-campus'] },
  { id: 'developer', label: 'Developer Collection', badge: 'Developer', badgeColor: '#24292e', description: 'Github stats, monospace, and SDE layouts', templates: ['faang-engineering', 'github-developer'] }
];

export const ALL_TEMPLATE_IDS = [
  'harvard-classic', 'modern-ats-pro', 'faang-engineering', 'executive-professional',
  'campus-placement', 'internship-hunter', 'fresher-plus', 'finance-pro',
  'product-consulting', 'creative-professional', 'clean-minimal-ats', 'two-column-ats',
  'stanford-ats', 'modern-blue', 'creative-studio', 'executive-black',
  'startup-neon', 'university-campus', 'github-developer', 'terminal-hacker'
];
